import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Menu,
  User,
  LogOut,
  LayoutDashboard,
  Award,
  Search,
  CalendarCheck,
} from "lucide-react";
import { useState } from "react";
import { useUserProfiles } from "@/hooks/useUserProfiles";
import { useAuth } from "@/components/auth/AuthProvider";
import { AuthDialog } from "@/components/auth/AuthDialog";
import { GTranslateWidget } from "@/components/GTranslateWidget";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { Separator } from "@/components/ui/separator";
import logo from "@/assets/ekselencia-logo-main.png";
import { useNavigationContext } from "@/hooks/useNavigationContext";
import NavDropdownMenu from "@/components/navigation/NavDropdownMenu";
import StickySecondaryNav from "@/components/navigation/StickySecondaryNav";
import { cn } from "@/lib/utils";
import { DescubrimientoCaracteristicas } from "@/components/ayuda";

const Navigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { hasAnyProfile, primaryProfile } = useUserProfiles();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Use context-aware navigation
  const { context, menuConfig, contextId } = useNavigationContext();

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/directory?search=${encodeURIComponent(searchQuery)}`);
    }
  };

  const isActive = (path: string) => location.pathname === path;

  const handleCtaClick = () => {
    if (menuConfig.cta.action === 'auth') {
      setAuthDialogOpen(true);
    } else if (menuConfig.cta.path) {
      navigate(menuConfig.cta.path);
    }
  };

  return (
    <>
      <GTranslateWidget />
      {/* GTranslate Widget Container - Fixed Position Bottom Left */}
      <div className="gtranslate_wrapper fixed bottom-4 left-4 z-[9999]" />

      {/* Sticky Secondary Nav - appears on scroll */}
      <StickySecondaryNav onAuthClick={() => setAuthDialogOpen(true)} />

      <nav className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-primary to-accent backdrop-blur-lg border-b border-white/20 shadow-xl">
        <div className="container mx-auto px-4">
          {/* First Row - Logo, Search Bar, User Actions */}
          <div className="flex items-center gap-4 h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center flex-shrink-0">
              <img src={logo} alt="Ekselencia" className="h-[69px] w-auto" />
            </Link>

            {/* Big Search Bar - Desktop */}
            <form onSubmit={handleSearch} className="hidden lg:flex flex-1 max-w-2xl mx-auto">
              <div className="relative w-full">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/60" />
                <Input
                  type="search"
                  placeholder="Buscar gimnasios, entrenadores, eventos, tribus..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full h-12 pl-12 pr-4 bg-white/10 border-2 border-white/30 text-white placeholder:text-white/60 focus:bg-white/20 focus:border-white/50 rounded-full text-base"
                />
              </div>
            </form>

            <div className="hidden lg:flex items-center space-x-2 flex-shrink-0">
              {/* Feature Discovery */}
              <DescubrimientoCaracteristicas className="mr-2" />
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="nav" size="sm" className="gap-2">
                      <User className="w-4 h-4" />
                      Mi Cuenta
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-popover">
                    <DropdownMenuItem onClick={() => navigate("/dashboard")}>
                      <LayoutDashboard className="w-4 h-4 mr-2" />
                      Dashboard
                    </DropdownMenuItem>
                    {hasAnyProfile && primaryProfile && (
                      <DropdownMenuItem onClick={() => navigate(primaryProfile.dashboardPath)}>
                        <CalendarCheck className="w-4 h-4 mr-2" />
                        Sistema de Reservas
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={() => navigate("/affiliate/dashboard")}>
                      <Award className="w-4 h-4 mr-2" />
                      Panel de Afiliados
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={signOut}>
                      <LogOut className="w-4 h-4 mr-2" />
                      Cerrar Sesión
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Button variant="nav" size="sm" onClick={() => setAuthDialogOpen(true)}>
                  Iniciar Sesión
                </Button>
              )}
              <Button 
                size="sm"
                variant="navActive"
                onClick={handleCtaClick}
              >
                {menuConfig.cta.label}
              </Button>
            </div>

            <AuthDialog open={authDialogOpen} onOpenChange={setAuthDialogOpen} />

            {/* Mobile Menu Sheet */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="lg:hidden text-white hover:bg-white/10 ml-auto"
                  aria-label="Toggle menu"
                >
                  <Menu className="w-6 h-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] bg-gradient-to-b from-primary to-accent border-l border-white/20 overflow-y-auto">
                <SheetHeader>
                  <SheetTitle className="text-white text-left flex items-center gap-2">
                    <context.icon className="w-4 h-4" />
                    {context.label}
                  </SheetTitle>
                </SheetHeader>

                {/* Mobile Search */}
                <div className="mt-6 mb-6">
                  <form onSubmit={handleSearch}>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/60" />
                      <Input
                        type="search"
                        placeholder="Buscar..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 bg-white/10 border-2 border-white/30 text-white placeholder:text-white/60 focus:bg-white/20 focus:border-white/50 rounded-lg"
                      />
                    </div>
                  </form>
                </div>

                {/* Navigation Section - Context-aware */}
                <div className="space-y-1 mb-6">
                  <p className="text-xs font-semibold text-white/60 uppercase tracking-wider px-2 mb-2">
                    {context.label}
                  </p>
                  {menuConfig.menuItems.map((item) => (
                    <Link
                      key={item.path + item.label}
                      to={item.path}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <Button
                        variant="ghost"
                        className={cn(
                          "w-full justify-start text-white hover:bg-white/10",
                          isActive(item.path) && "bg-white/20 font-semibold",
                          item.special && "bg-destructive hover:bg-destructive/90 text-white font-bold"
                        )}
                      >
                        <item.icon className="w-4 h-4 mr-3" />
                        {item.label}
                      </Button>
                    </Link>
                  ))}
                </div>

                <Separator className="bg-white/20 my-4" />

                {/* User Actions Section */}
                <div className="space-y-1">
                  <p className="text-xs font-semibold text-white/60 uppercase tracking-wider px-2 mb-2">Cuenta</p>
                  {user ? (
                    <>
                      <Button 
                        variant="ghost" 
                        className="w-full justify-start text-white hover:bg-white/10"
                        onClick={() => {
                          navigate("/dashboard");
                          setMobileMenuOpen(false);
                        }}
                      >
                        <LayoutDashboard className="w-4 h-4 mr-3" />
                        Dashboard
                      </Button>
                      {hasAnyProfile && primaryProfile && (
                        <Button 
                          variant="ghost" 
                          className="w-full justify-start text-white hover:bg-white/10"
                          onClick={() => {
                            navigate(primaryProfile.dashboardPath);
                            setMobileMenuOpen(false);
                          }}
                        >
                          <CalendarCheck className="w-4 h-4 mr-3" />
                          Sistema de Reservas
                        </Button>
                      )}
                      <Button 
                        variant="ghost" 
                        className="w-full justify-start text-white hover:bg-white/10"
                        onClick={() => {
                          navigate("/affiliate/dashboard");
                          setMobileMenuOpen(false);
                        }}
                      >
                        <Award className="w-4 h-4 mr-3" />
                        Panel de Afiliados
                      </Button>
                      <Button 
                        variant="ghost" 
                        className="w-full justify-start text-white hover:bg-white/10"
                        onClick={() => {
                          signOut();
                          setMobileMenuOpen(false);
                        }}
                      >
                        <LogOut className="w-4 h-4 mr-3" />
                        Cerrar Sesión
                      </Button>
                    </>
                  ) : (
                    <Button 
                      variant="ghost" 
                      className="w-full justify-start text-white hover:bg-white/10"
                      onClick={() => {
                        setAuthDialogOpen(true);
                        setMobileMenuOpen(false);
                      }}
                    >
                      <User className="w-4 h-4 mr-3" />
                      Iniciar Sesión
                    </Button>
                  )}
                  
                  <Separator className="bg-white/20 my-4" />
                  
                  <Button 
                    className="w-full bg-white text-primary hover:bg-white/90 font-semibold"
                    onClick={() => {
                      handleCtaClick();
                      setMobileMenuOpen(false);
                    }}
                  >
                    {menuConfig.cta.label}
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>

          {/* Second Row - Context-aware Navigation with Dropdowns (Desktop Only) */}
          <div className="hidden lg:flex items-center justify-center py-2 border-t border-white/20">
            {/* Context Label */}
            {contextId !== 'home' && (
              <span className="text-xs text-white/60 uppercase tracking-wider mr-4 flex items-center gap-1">
                <context.icon className="w-3 h-3" />
                {context.label}
                <span className="text-white/40">›</span>
              </span>
            )}

            {/* Navigation Menu with Dropdowns */}
            <NavigationMenu>
              <NavigationMenuList className="gap-1">
                {menuConfig.menuItems.map((item) => (
                  item.hasDropdown && item.dropdownId && context.dropdowns[item.dropdownId] ? (
                    <NavigationMenuItem key={item.path + item.label}>
                      <NavigationMenuTrigger
                        className={cn(
                          "h-8 px-3 text-xs bg-transparent text-white/90 hover:bg-white/10 hover:text-white data-[state=open]:bg-white/20",
                          "focus:bg-white/10 focus:text-white"
                        )}
                      >
                        <item.icon className="w-3 h-3 mr-1.5" />
                        {item.label}
                      </NavigationMenuTrigger>
                      <NavigationMenuContent>
                        <NavDropdownMenu config={context.dropdowns[item.dropdownId]} />
                      </NavigationMenuContent>
                    </NavigationMenuItem>
                  ) : (
                    <NavigationMenuItem key={item.path + item.label}>
                      <Link to={item.path}>
                        <Button
                          variant={isActive(item.path) ? "navActive" : "nav"}
                          size="sm"
                          className={cn(
                            "h-8 text-xs",
                            item.special && "bg-destructive text-destructive-foreground hover:bg-destructive/90 border-none"
                          )}
                        >
                          <item.icon className="w-3 h-3 mr-1.5" />
                          {item.label}
                        </Button>
                      </Link>
                    </NavigationMenuItem>
                  )
                ))}
              </NavigationMenuList>
            </NavigationMenu>
          </div>
        </div>
      </nav>
    </>
  );
};

export default Navigation;
